import { withTranslation } from '@application/i18n/with_translation';
import { Button, FileViewer, Flex, Icon, Text, VisuallyHidden } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useResolve } from '@visiba/instantiation';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import {
	ChangeEvent,
	createContext,
	DragEvent,
	MouseEvent,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import styled from 'styled-components';
import { AttachedFile } from './attached_file.component';
import type { FileManagerServiceReadyState } from './file_manager.service';
import { FileManagerService } from './file_manager.service';
import { useFileWorkerList } from './use_file_worker_list';

interface Props extends PropsWithChildren {
	identityEndpointPath: string;
	onStateChange?: (state: FileManagerServiceReadyState) => void;
}

const IdentityEndpointPathCtx = createContext('missing-path');

function FileUploadRoot({ identityEndpointPath, onStateChange, children }: Props) {
	const fileManagerService = useResolve(FileManagerService);
	const [state, setState] = useState<FileManagerServiceReadyState>(fileManagerService.readyState.value);

	useEffect(
		function whenStateChanges() {
			if (state !== fileManagerService.readyState.value) {
				setState(fileManagerService.readyState.value);
				onStateChange?.(fileManagerService.readyState.value);
			}
		},
		[fileManagerService.readyState.value, onStateChange, state],
	);

	return <IdentityEndpointPathCtx.Provider value={identityEndpointPath}>{children}</IdentityEndpointPathCtx.Provider>;
}

const rootElements = {
	attachedFilesContainer: styled.div`
		margin-top: ${tokens.spacing.x2};
		display: flex;
		flex-wrap: wrap;
		gap: ${tokens.spacing.x1_5};
	`,
	attachedFileMotion: styled(motion.div)`
		width: 100%;
		transform-origin: bottom left 60px;
		min-width: 0;
	`,
};

const motionVariants: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.9,
		transition: {
			stiffness: 500,
			damping: 40,
			type: 'spring',
		},
	},
	visible: (custom: number) => ({
		scale: 1,
		opacity: 1,
		transition: {
			delay: custom * 0.025,
			stiffness: 500,
			damping: 40,
			type: 'spring',
		},
	}),
};

function FileUploadList() {
	const fileManagerService = useResolve(FileManagerService);
	const fileWorkers = useFileWorkerList(fileManagerService);

	function handleRemoveFileClick(id: string): void {
		fileManagerService.dropWorker(id);
	}

	return (
		<>
			{fileWorkers.length > 0 ? (
				<FileViewer.Root>
					<rootElements.attachedFilesContainer>
						<AnimatePresence presenceAffectsLayout>
							{fileWorkers.map((worker, index) => (
								<rootElements.attachedFileMotion
									key={worker.id}
									custom={index}
									layout
									variants={motionVariants}
									initial='hidden'
									animate='visible'
									exit='hidden'
								>
									<AttachedFile worker={worker} onRemoveClick={handleRemoveFileClick} />
								</rootElements.attachedFileMotion>
							))}
						</AnimatePresence>
					</rootElements.attachedFilesContainer>
				</FileViewer.Root>
			) : null}
		</>
	);
}

const IsWithinFileUploadDropZoneCtx = createContext(false);

const StyledDropZone = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: ${tokens.size.x1_5} ${tokens.size.x2};
	width: 100%;
	border: 1px dashed ${tokens.color.border.default};
	border-radius: ${tokens.borderRadius.medium};
	min-height: ${tokens.size.x9};

	&[data-is-dropping='true'] {
		border-color: ${tokens.color.border.interactive};
		background: ${tokens.color.bg.interactiveMutedActive};
	}

	@media (hover: hover) and (pointer: fine) {
		&:hover {
			border-color: ${tokens.color.border.interactive};
		}
		&:has(input[type='file']:focus-visible) {
			outline: 2px solid ${tokens.color.focus};
			outline-offset: 2px;
		}
	}
`;

const StyledDropContent = styled.div`
	height: 48px;
	overflow: hidden;
	position: relative;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${tokens.color.content.interactive};
`;

const StyledDropContentAnimate = styled(motion.div)`
	display: flex;
	align-items: center;
	flex-direction: column;
	position: absolute;
	height: 150%;
	bottom: 13px;
	left: 0;
	right: 0;
	justify-content: space-between;
`;

function FileUploadDropZone(props: PropsWithChildren) {
	const ref = useRef<HTMLDivElement>(null);
	const identityEndpointPath = useContext(IdentityEndpointPathCtx);
	const fileManagerService = useResolve(FileManagerService);
	const [isDropping, setIsDropping] = useState(false);

	function handleDropDrag(e: DragEvent) {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';

		setIsDropping(true);
	}

	// Propagate the click to potentially hidden input element
	function handleClick(_: MouseEvent) {
		ref.current?.querySelector('button')?.click();
	}

	function handleDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		setIsDropping(false);

		if (event.dataTransfer.files.length <= 0) return;

		fileManagerService.assignWorker(event.dataTransfer.files, identityEndpointPath);

		queueMicrotask(async () => {
			fileManagerService.updateReadyState();

			const workers = fileManagerService.peakWorkerList();
			const promises = [];
			for (const worker of workers) {
				promises.push(worker.upload());
			}

			// TODO: callback to parent component? Or the upload should be handled in the parent component?
			try {
				await Promise.allSettled(promises);
			} finally {
				fileManagerService.updateReadyState();
			}
		});
	}

	function handleDragLeave() {
		setIsDropping(false);
	}

	return (
		<StyledDropZone
			data-is-dropping={isDropping}
			ref={ref}
			onClick={handleClick}
			onDrop={handleDrop}
			onDragOver={handleDropDrag}
			onDragLeave={handleDragLeave}
		>
			<StyledDropContent>
				<StyledDropContentAnimate
					variants={{
						dropping: {
							y: 52,
						},
						idle: {
							y: 0,
							transition: {
								delay: 0.1,
							},
						},
					}}
					animate={isDropping ? 'dropping' : 'idle'}
				>
					<Flex direction='row' gap={tokens.size.x1}>
						<Icon type='upload' size='small' />
						<Text variant='labelSemiBold'>{withTranslation('Feature.FileManager.DropZone')}</Text>
					</Flex>

					<Flex direction='row' gap={tokens.size.x1}>
						<Icon type='paperClip' size='small' />
						<Text variant='labelSemiBold'>{withTranslation('Feature.FileManager.AttachFile')}</Text>
					</Flex>
				</StyledDropContentAnimate>
			</StyledDropContent>

			<IsWithinFileUploadDropZoneCtx.Provider value={true}>{props.children}</IsWithinFileUploadDropZoneCtx.Provider>
		</StyledDropZone>
	);
}

function FileUploadButton() {
	const fileManagerService = useResolve(FileManagerService);
	const identityEndpointPath = useContext(IdentityEndpointPathCtx);

	const inputRef = useRef<HTMLInputElement>(null);
	const hiddenFileInput = useRef<HTMLInputElement>(null);

	function handleUploadFileButtonClick(): void {
		hiddenFileInput.current?.click();
	}

	const uploadFiles = useCallback(
		function uploadFiles(fileList: Nullable<FileList>) {
			if (fileList != null) {
				fileManagerService.assignWorker(fileList, identityEndpointPath);
			}

			queueMicrotask(async () => {
				fileManagerService.updateReadyState();

				const workers = fileManagerService.peakWorkerList();
				const promises = [];
				for (const worker of workers) {
					promises.push(worker.upload());
				}

				// TODO: callback to parent component? Or the upload should be handled in the parent component?
				try {
					await Promise.allSettled(promises);
				} finally {
					fileManagerService.updateReadyState();
				}
			});
		},
		[fileManagerService, identityEndpointPath],
	);

	useEffect(
		function bindPasteImage() {
			const abortController = new AbortController();

			document.addEventListener(
				'paste',
				(e) => {
					const clipboardItems = e.clipboardData?.files ?? new FileList();

					if (clipboardItems.length > 0) {
						e.preventDefault();
						uploadFiles(clipboardItems);
					}
				},
				{
					signal: abortController.signal,
				},
			);

			return () => {
				abortController.abort();
			};
		},
		[uploadFiles],
	);

	function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
		uploadFiles(event.target.files);

		event.target.value = '';
		inputRef.current?.focus();
	}

	const button = (
		<Button variant='ghost' size='medium' icon='paperClip' iconPosition='leading' onClick={handleUploadFileButtonClick}>
			{withTranslation('PW.Component.Chat.Button.File')}
		</Button>
	);

	return (
		<>
			{IsWithinFileUploadDropZoneCtx ? <VisuallyHidden>{button}</VisuallyHidden> : button}
			<VisuallyHidden>
				<input aria-hidden='true' type='file' ref={hiddenFileInput} onChange={handleFileChange} multiple tabIndex={0} />
			</VisuallyHidden>
		</>
	);
}

export const FileUpload = {
	Root: FileUploadRoot,
	List: FileUploadList,
	DropZone: FileUploadDropZone,
	Button: FileUploadButton,
} as const;
